/**
 * BrandingSettings Component
 * Main page for organization branding management with tabs for different branding areas
 */

import React, { useState, useEffect } from 'react';
import {
  Palette,
  Mail,
  Share2,
  FileText,
  MessageSquare,
  RefreshCw,
  AlertCircle,
  CheckCircle,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../ui/tabs';
import { GeneralBrandingTab } from './GeneralBrandingTab';
import { EmailBrandingTab } from './EmailBrandingTab';
import { SocialMediaBrandingTab } from './SocialMediaBrandingTab';
import {
  brandingAPI,
  OrganizationBranding,
  getDefaultBranding,
} from '@/services/api/branding';

interface BrandingSettingsProps {
  organizationId: number;
  organizationName: string;
}

export function BrandingSettings({ organizationId, organizationName }: BrandingSettingsProps) {
  const [branding, setBranding] = useState<Partial<OrganizationBranding>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  useEffect(() => {
    fetchBranding();
  }, [organizationId]);

  const fetchBranding = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await brandingAPI.getBranding(organizationId);
      if (data) {
        setBranding(data);
      } else {
        // Initialize with defaults if no branding exists
        setBranding({
          organization_id: organizationId,
          ...getDefaultBranding(),
        });
      }
    } catch (err: any) {
      console.error('Error fetching branding:', err);
      setError('Failed to load branding settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (updates: Partial<OrganizationBranding>) => {
    setBranding((prev) => ({ ...prev, ...updates }));
  };

  const saveBranding = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      if (branding.id) {
        // Update existing branding
        await brandingAPI.updateBranding(organizationId, branding);
      } else {
        // Create new branding
        const created = await brandingAPI.createBranding({
          organization_id: organizationId,
          ...branding,
        });
        setBranding(created);
      }
      setSuccess('Branding settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);
      // Notify BrandingContext to re-fetch so sidebar/colors update immediately
      window.dispatchEvent(new Event('brandingChanged'));
    } catch (err: any) {
      console.error('Error saving branding:', err);
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((e: any) => e.msg || String(e)).join('; '));
      } else {
        setError('Failed to save branding settings');
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewEmail = async () => {
    try {
      const preview = await brandingAPI.previewEmail(organizationId, {
        template_type: 'outreach',
        subject: 'Sample Email Preview',
        content: '<p>This is a sample email to preview your branding settings. The colors, fonts, and layout shown here will be applied to all outgoing emails.</p>',
        recipient_name: 'John Doe',
        cta_text: 'Learn More',
        cta_url: 'https://example.com',
      });
      setPreviewHtml(preview.html);
      setShowPreview(true);
    } catch (err: any) {
      console.error('Error generating preview:', err);
      setError('Failed to generate email preview');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading branding settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            Organization Branding
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Customize how your organization appears in communications and campaigns
          </p>
        </div>
        <button
          onClick={fetchBranding}
          className="p-2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <RefreshCw className="w-5 h-5" />
        </button>
      </div>

      {/* Status Messages */}
      {error && (
        <div className="p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 rounded-lg flex items-center">
          <CheckCircle className="w-5 h-5 mr-2" />
          {success}
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="general" className="w-full">
        <TabsList className="flex space-x-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg mb-6">
          <TabsTrigger
            value="general"
            className="flex items-center px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow data-[state=active]:text-blue-600 dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-blue-400 text-gray-600 dark:text-gray-400"
          >
            <Palette className="w-4 h-4 mr-2" />
            General
          </TabsTrigger>
          <TabsTrigger
            value="email"
            className="flex items-center px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow data-[state=active]:text-blue-600 dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-blue-400 text-gray-600 dark:text-gray-400"
          >
            <Mail className="w-4 h-4 mr-2" />
            Email
          </TabsTrigger>
          <TabsTrigger
            value="social"
            className="flex items-center px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow data-[state=active]:text-blue-600 dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-blue-400 text-gray-600 dark:text-gray-400"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Social Media
          </TabsTrigger>
          <TabsTrigger
            value="communications"
            className="flex items-center px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow data-[state=active]:text-blue-600 dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-blue-400 text-gray-600 dark:text-gray-400"
          >
            <MessageSquare className="w-4 h-4 mr-2" />
            SMS/WhatsApp/Voice
          </TabsTrigger>
          <TabsTrigger
            value="documents"
            className="flex items-center px-4 py-2 text-sm font-medium rounded-md data-[state=active]:bg-white data-[state=active]:shadow data-[state=active]:text-blue-600 dark:data-[state=active]:bg-gray-700 dark:data-[state=active]:text-blue-400 text-gray-600 dark:text-gray-400"
          >
            <FileText className="w-4 h-4 mr-2" />
            Documents
          </TabsTrigger>
        </TabsList>

        {/* General Branding Tab */}
        <TabsContent value="general">
          <GeneralBrandingTab
            branding={branding}
            onChange={handleChange}
            onSave={saveBranding}
            saving={saving}
            organizationName={organizationName}
            organizationId={organizationId}
          />
        </TabsContent>

        {/* Email Branding Tab */}
        <TabsContent value="email">
          <EmailBrandingTab
            branding={branding}
            onChange={handleChange}
            onSave={saveBranding}
            onPreview={handlePreviewEmail}
            saving={saving}
            organizationName={organizationName}
          />
        </TabsContent>

        {/* Social Media Branding Tab */}
        <TabsContent value="social">
          <SocialMediaBrandingTab
            branding={branding}
            onChange={handleChange}
            onSave={saveBranding}
            saving={saving}
            organizationName={organizationName}
          />
        </TabsContent>

        {/* SMS/Voice Branding Tab */}
        <TabsContent value="communications">
          <CommunicationsBrandingTab
            branding={branding}
            onChange={handleChange}
            onSave={saveBranding}
            saving={saving}
          />
        </TabsContent>

        {/* Document Branding Tab */}
        <TabsContent value="documents">
          <DocumentsBrandingTab
            branding={branding}
            onChange={handleChange}
            onSave={saveBranding}
            saving={saving}
          />
        </TabsContent>
      </Tabs>

      {/* Email Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                Email Preview
              </h3>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              >
                <span className="sr-only">Close</span>
                &times;
              </button>
            </div>
            <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
              <iframe
                srcDoc={previewHtml}
                className="w-full h-[600px] border border-gray-200 dark:border-gray-700 rounded"
                title="Email Preview"
              />
            </div>
            <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// SMS/Voice Branding Tab (Inline)
// ============================================================================

interface CommunicationsBrandingTabProps {
  branding: Partial<OrganizationBranding>;
  onChange: (updates: Partial<OrganizationBranding>) => void;
  onSave: () => Promise<void>;
  saving: boolean;
}

function CommunicationsBrandingTab({
  branding,
  onChange,
  onSave,
  saving,
}: CommunicationsBrandingTabProps) {
  const handleChange = (field: keyof OrganizationBranding, value: any) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-8">
      {/* SMS Branding */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <MessageSquare className="w-5 h-5 mr-2 text-green-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">SMS Branding</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              SMS Sender Name
            </label>
            <input
              type="text"
              value={branding.sms_sender_name || ''}
              onChange={(e) => handleChange('sms_sender_name', e.target.value.slice(0, 11))}
              placeholder="YourOrg"
              maxLength={11}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Max 11 characters. Alphanumeric sender ID.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              SMS Signature
            </label>
            <input
              type="text"
              value={branding.sms_signature || ''}
              onChange={(e) => handleChange('sms_signature', e.target.value)}
              placeholder="- Your Organization Team"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Appended to outgoing SMS messages
            </p>
          </div>
        </div>
      </div>

      {/* WhatsApp Branding */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <MessageSquare className="w-5 h-5 mr-2 text-green-500" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">WhatsApp Business Profile</h3>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              About
            </label>
            <input
              type="text"
              value={(branding as any).whatsapp_about || ''}
              onChange={(e) => handleChange('whatsapp_about' as any, e.target.value)}
              placeholder="Brief description shown on your WhatsApp profile"
              maxLength={139}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">Max 139 characters. Displayed in WhatsApp business profile.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={(branding as any).whatsapp_description || ''}
              onChange={(e) => handleChange('whatsapp_description' as any, e.target.value)}
              rows={2}
              placeholder="Detailed description of your organization for WhatsApp"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Business Address
            </label>
            <input
              type="text"
              value={(branding as any).whatsapp_address || ''}
              onChange={(e) => handleChange('whatsapp_address' as any, e.target.value)}
              placeholder="123 Main St, City, State ZIP"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">Displayed on your WhatsApp Business profile page.</p>
          </div>
        </div>
      </div>

      {/* Voice Branding */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <MessageSquare className="w-5 h-5 mr-2 text-blue-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Voice Branding</h3>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Voice Greeting
            </label>
            <textarea
              value={branding.voice_greeting || ''}
              onChange={(e) => handleChange('voice_greeting', e.target.value)}
              rows={2}
              placeholder="Thank you for calling [Organization Name]. How can we help you today?"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Hold Message
            </label>
            <textarea
              value={branding.voice_hold_message || ''}
              onChange={(e) => handleChange('voice_hold_message', e.target.value)}
              rows={2}
              placeholder="Please hold while we connect you to the next available representative."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Voicemail Greeting
            </label>
            <textarea
              value={branding.voice_voicemail_greeting || ''}
              onChange={(e) => handleChange('voice_voicemail_greeting', e.target.value)}
              rows={2}
              placeholder="You've reached [Organization Name]. Please leave a message and we'll get back to you."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CheckCircle className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// Document Branding Tab (Inline)
// ============================================================================

interface DocumentsBrandingTabProps {
  branding: Partial<OrganizationBranding>;
  onChange: (updates: Partial<OrganizationBranding>) => void;
  onSave: () => Promise<void>;
  saving: boolean;
}

function DocumentsBrandingTab({
  branding,
  onChange,
  onSave,
  saving,
}: DocumentsBrandingTabProps) {
  const handleChange = (field: keyof OrganizationBranding, value: any) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-8">
      {/* Document Header */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <FileText className="w-5 h-5 mr-2 text-purple-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Document Branding</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Document Header Logo URL
            </label>
            <input
              type="text"
              value={branding.document_header_logo_url || ''}
              onChange={(e) => handleChange('document_header_logo_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Letterhead Image URL
            </label>
            <input
              type="text"
              value={branding.document_letterhead_url || ''}
              onChange={(e) => handleChange('document_letterhead_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Document Watermark URL
            </label>
            <input
              type="text"
              value={branding.document_watermark_url || ''}
              onChange={(e) => handleChange('document_watermark_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Semi-transparent watermark for PDF documents
            </p>
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Document Footer Text
            </label>
            <textarea
              value={branding.document_footer_text || ''}
              onChange={(e) => handleChange('document_footer_text', e.target.value)}
              rows={2}
              placeholder="Your Organization Name | Address | Phone | Website"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>
        </div>
      </div>

      {/* Portal Branding */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-center mb-6">
          <FileText className="w-5 h-5 mr-2 text-cyan-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Member Portal Branding</h3>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Welcome Message
            </label>
            <textarea
              value={branding.portal_welcome_message || ''}
              onChange={(e) => handleChange('portal_welcome_message', e.target.value)}
              rows={3}
              placeholder="Welcome to your member portal. Here you can access your resources and track your progress."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Portal Banner Image URL
            </label>
            <input
              type="text"
              value={branding.portal_banner_image_url || ''}
              onChange={(e) => handleChange('portal_banner_image_url', e.target.value)}
              placeholder="https://..."
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              Recommended: 1200x300px
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={onSave}
          disabled={saving}
          className="flex items-center px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CheckCircle className="w-4 h-4 mr-2" />
          )}
          Save Changes
        </button>
      </div>
    </div>
  );
}

export default BrandingSettings;
